import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
} from '@material-ui/core'

// Icons
import ActivityIcon from '@material-ui/icons/DonutLarge'
import ChevronLeftIconIcon from '@material-ui/icons/ChevronLeft'
import VehiclesIcon from '@material-ui/icons/DriveEta'
import UsersIcon from '@material-ui/icons/People'
import AnalyticsIcon from '@material-ui/icons/Assessment'
import SettingsIcon from '@material-ui/icons/Settings'

import Box from '../Box'
import Typography from '../Typography'

import capitalizeFirstChar from '../utilities/capitalizeFirstChar'

const navBarWidth = 50
const subNavWidth = 270

const useStyles = makeStyles(theme => ({
  currentTabIndicator: {
    position: 'relative',
    backgroundColor: theme.palette.secondary.main,
    height: '50px',
    width: '8px',
    marginRight: '4px',
  },
  navBar: {
    width: navBarWidth,
    flexShrink: 0,
  },
  navBarPaper: {
    boxShadow: '1px 2px 4px rgba(0, 0, 0, 0.18)',
    borderTopRightRadius: 0,
    width: navBarWidth,
    zIndex: 1201,
  },
  navListItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50px',
    width: '50px',
    margin: '10px 0',
  },
  removeListItemPadding: {
    padding: 0,
  },
  subNav: {
    background: '#f5f5f4',
    marginTop: '8px',
    marginLeft: '50px',
    minWidth: subNavWidth,
    overflow: 'hidden',
  },
  subNavHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '10px',
  },
  subNavTitle: {
    display: 'flex',
    alignItems: 'center',
  },
  subNavTitleIcon: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '10px',
    marginLeft: '12px',
  },
  subNavSubHeader: {
    color: '#717071',
    marginLeft: '16px',
    marginTop: '4px',
    marginBottom: '6px',
  },
  subNavListItem: {
    marginLeft: '10px',
    marginBottom: '2px',
  },
  subNavDivider: {
    margin: '14px 0',
  },
}))

const SubNavItems = ({ activeTab, classes, handleSubNavItemClick, orderedNav, ...overrides }) =>
  orderedNav.map(subNav => {
    if (subNav.key === activeTab) {
      return Object.entries(subNav.menuSections).map(([key, value]) => (
        <>
          <Typography className={classes.subNavSubHeader} variant="body1">
            {key}
          </Typography>
          {value.map(page => (
            <>
              <ListItem button classes={{ root: classes.subNavListItem }} key={page.key}>
                <Link
                  component="button"
                  onClick={() => {
                    handleSubNavItemClick({ key: subNav.key, path: page.route })
                  }}
                  underline="none"
                  variant="body1"
                  {...overrides}
                >
                  {page.translationDefault}
                </Link>
              </ListItem>
            </>
          ))}
          <Divider className={classes.subNavDivider} />
        </>
      ))
    }

    return null
  })

const NavbarWrapper = ({ currentNavItem, sortedNavs, routeTo, ...overrides }) => {
  const classes = useStyles()
  const [activeTab, setActiveTab] = useState(currentNavItem || 'activity')
  const [currentTab, setCurrentTab] = useState(currentNavItem || 'activity')
  const [subNavOpen, setSubNavOpen] = useState(false)

  const LinkProps = overrides.LinkProps

  const navSectionMap = {
    activity: {
      icon: (
        <ActivityIcon
          color={activeTab === 'activity' ? 'primary' : 'text'}
          style={{
            fontSize: '1.8rem',
          }}
        />
      ),
      order: 1,
    },
    vehicles: {
      icon: (
        <VehiclesIcon
          color={activeTab === 'vehicles' ? 'primary' : 'text'}
          style={{
            fontSize: '1.8rem',
          }}
        />
      ),
      order: 2,
    },
    users: {
      icon: (
        <UsersIcon
          color={activeTab === 'users' ? 'primary' : 'text'}
          style={{
            fontSize: '1.8rem',
          }}
        />
      ),
      order: 3,
    },
    reports: {
      icon: (
        <AnalyticsIcon
          color={activeTab === 'reports' ? 'primary' : 'text'}
          style={{
            fontSize: '1.8rem',
          }}
        />
      ),
      order: 4,
    },
    settings: {
      icon: (
        <SettingsIcon
          color={activeTab === 'settings' ? 'primary' : 'text'}
          style={{
            fontSize: '1.8rem',
          }}
        />
      ),
      order: 5,
    },
  }
  const orderedNav = sortedNavs.sort(
    (a, b) => navSectionMap[a.key].order - navSectionMap[b.key].order
  )

  const handleNavItemClick = key => setActiveTab(key)
  const handleSubNavOpen = () => setSubNavOpen(true)
  const handleSubNavClose = () => setSubNavOpen(false)
  const handleSubNavItemClick = ({ key, path }) => {
    if (routeTo) {
      routeTo(path)
    }
    setActiveTab(key)
    setCurrentTab(key)
    setSubNavOpen(false)
  }

  return (
    <div>
      <CssBaseline />
      <Drawer
        className={classes.navBar}
        variant="permanent"
        classes={{
          paper: classes.navBarPaper,
        }}
        anchor="left"
        {...overrides}
      >
        <List classes={{ root: classes.removeListItemPadding }}>
          {orderedNav.map(nav => (
            <ListItem
              button
              classes={{ root: classes.removeListItemPadding }}
              dense={false}
              disableGutters
              key={nav.key}
              onClick={() => {
                handleNavItemClick(nav.key)
                handleSubNavOpen()
              }}
            >
              <ListItemIcon className={classes.navListItem}>
                <>
                  {currentTab === nav.key ? <div className={classes.currentTabIndicator} /> : null}
                  {navSectionMap[nav.key].icon}
                </>
              </ListItemIcon>
            </ListItem>
          ))}
        </List>
      </Drawer>
      <Drawer
        anchor="left"
        classes={{ paper: classes.subNav }}
        open={subNavOpen}
        variant="persistent"
      >
        <Box className={classes.subNavHeader}>
          <div className={classes.subNavTitle}>
            <div className={classes.subNavTitleIcon}>{navSectionMap[activeTab].icon}</div>
            <Typography variant="h5">{capitalizeFirstChar(activeTab)}</Typography>
          </div>
          <IconButton onClick={handleSubNavClose}>
            <ChevronLeftIconIcon />
          </IconButton>
        </Box>
        <List>
          <SubNavItems
            activeTab={activeTab}
            classes={classes}
            handleSubNavItemClick={handleSubNavItemClick}
            orderedNav={orderedNav}
            {...LinkProps}
          />
        </List>
      </Drawer>
    </div>
  )
}

export default NavbarWrapper
